import React from "react";
import styled from "@emotion/styled";
import { Link } from "gatsby";
import { theme } from "../utils/styles";
import { BLOG_POSTS } from "../utils/BlogPosts";
import ScindoRoundSummaryImage from "../assets/Scindo_round_summary.png";
import MarangaImage from "../assets/MM_hiring cover photo.jpg";
import SynbiTECHImage from "../assets/Synbitech_speaker announcement.jpg";
import EITFANImage from "../assets/eit-fan-2026-paris-cohort.png";

const Wrap = styled.aside`
  font-family: Nunito, sans-serif;
  padding: 0 24px;
  width: 100%;
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  padding-bottom: 12px;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  gap: 20px;
  width: max-content;
  min-width: 100%;
`;

const Card = styled.li`
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.45s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
  width: 240px;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.16);
  }
`;

const CardImageContainer = styled.div`
  width: 100%;
  height: 120px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%);
    z-index: 1;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(0deg, ${theme.primary}55 0%, ${theme.primary}00 65%);
    z-index: 2;
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  .blog-card:hover &::before {
    opacity: 1;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);

  .blog-card:hover & {
    transform: scale(1.08);
  }
`;

const CardContent = styled.div`
  padding: 18px;
`;

const Category = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${theme.primary};
  margin-bottom: 6px;
`;

const PostLink = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  
  &:hover {
    text-decoration: none;
  }
`;

const Title = styled.h3`
  margin: 0 0 10px 0;
  font-weight: 700;
  font-size: 1.1rem;
  line-height: 1.25;
  color: ${theme.black};
  font-family: Nunito, sans-serif;
  transition: color 0.3s ease;

  .blog-card:hover & {
    color: ${theme.primary};
  }
`;

const Meta = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 12px;
  font-family: Nunito, sans-serif;
  font-weight: 500;
`;

const Excerpt = styled.p`
  margin: 0;
  color: #4a4a4a;
  font-family: Nunito, sans-serif;
  line-height: 1.5;
  font-size: 0.9rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;



export default function BlogList() {
  // Sort posts by date (newest first) to ensure newest appears on the left
  const sortedPosts = [...BLOG_POSTS].sort((a, b) => {
    if (!a.date || !b.date) return 0;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  // Map image filenames to imported images
  const imageMap: Record<string, any> = {
    "Scindo_round_summary.png": ScindoRoundSummaryImage,
    "MM_hiring cover photo.jpg": MarangaImage,
    "Synbitech_speaker announcement.jpg": SynbiTECHImage,
    "eit-fan-2026-paris-cohort.png": EITFANImage,
  };

  return (
    <Wrap>
      <ScrollContainer>
        <List>
          {sortedPosts.map((p, i) => {
            const postImage = p.image ? imageMap[p.image] : ScindoRoundSummaryImage;
            return (
              <Card key={`${p.href}-${i}`} className="blog-card">
                <PostLink to={p.href}>
                  <CardImageContainer>
                    <CardImage src={postImage} alt={p.title} />
                  </CardImageContainer>
                  <CardContent>
                    <Category>Company News</Category>
                    <Title>{p.title}</Title>
                    {p.date ? <Meta>{new Date(p.date).toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}</Meta> : null}
                    {p.excerpt ? <Excerpt>{p.excerpt}</Excerpt> : null}
                  </CardContent>
                </PostLink>
              </Card>
            );
          })}
        </List>
      </ScrollContainer>
    </Wrap>
  );
}
